'use client'

import Link from 'next/link'
import { Logo } from '@/components/Logo/Logo'
import { CMSLink } from '@/components/Link'
import React, { ReactNode } from 'react'
import { LucideMail, LucidePhone, LucidePin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { formatDistance } from 'date-fns'
import { nl } from 'date-fns/locale'
import { PaymentBadges } from '@/components/PaymentBadges'

export default function FooterClient({
  projects,
  coaches,
  films,
  links,
  ticketEvents,
  buildTime,
  buildVersion,
}) {
  return (
    <footer className="border-t-4 border-primary bg-black dark:bg-card text-white">
      <div className="container pt-8 pb-8 gap-8 flex flex-col md:flex-row justify-between">
        <div className={'flex flex-col max-w-[380px]'}>
          <DonateWidget />
          <p className={'mt-1'}>Draag bij om onze projecten gratis voor een groep te realiseren!</p>
        </div>
        <div className="columns-1 lg:columns-2 xl:columns-3 xl:max-w-[50%] gap-4 grow">
          <LinksSection header={'Coaches'}>
            {coaches.map((coach, index) => (
              <div className={'inline'} key={coach.slug ?? 'coach' + index}>
                <Link
                  href={'/coach/' + coach.slug}
                  className={'inline hover:underline whitespace-nowrap'}
                >
                  {coach.name}
                </Link>
                {index < coaches.length - 1 && <span className={'select-none'}>, </span>}
              </div>
            ))}
          </LinksSection>
          <LinksSection header={'Projecten'}>
            {projects.map((project) => (
              <a
                key={project.slug}
                href={'/project/' + project.slug}
                className={'block hover:underline'}
              >
                {project.title}
              </a>
            ))}
          </LinksSection>
          <LinksSection header={'Tickets'}>
            {ticketEvents.map((event) => (
              <a
                key={event.slug}
                href={'/tickets/' + event.slug}
                className={'block hover:underline'}
              >
                {event.name}
              </a>
            ))}
          </LinksSection>
          {links.length ? (
            <LinksSection header={'Links'}>
              {links.map((link) => (
                <CMSLink key={link.id} {...link.link} className={'block hover:underline'}>
                  {link.link.title}
                </CMSLink>
              ))}
            </LinksSection>
          ) : (
            ''
          )}
          <LinksSection header={'Films'}>
            {films.map((film) => (
              <a key={film.slug} href={'/film/' + film.slug} className={'block hover:underline'}>
                {film.name}
              </a>
            ))}
          </LinksSection>
          <LinksSection header={'Contact'}>
            <div>
              <LucideMail className={'size-4 inline mr-1 -mt-[2px]'} />
              <a href={'mailto:info@jeugdfilms.nl'} className={'hover:underline'}>
                info@jeugdfilms.nl
              </a>
            </div>
            <div>
              <LucidePhone className={'size-4 inline mr-1 -mt-[2px]'} />
              <a href={'tel:+31851249688'} className={'hover:underline'}>
                0851-249688
              </a>
            </div>
            <Link href={'/contact'}>
              <Button size={'sm'} className={'mt-2 md:mt-4'} variant={'outline'}>
                <LucidePin fill={'currentColor'} className={'size-4 inline mr-2'} />
                Meeting inplannen
              </Button>
            </Link>
          </LinksSection>
          <LinksSection header={'Socials'}>
            <a href={'https://www.youtube.com/@jeugdfilms'} className={'block hover:underline'}>
              YouTube
            </a>
            <a
              href={'https://www.instagram.com/jeugdfilms.nl/'}
              className={'block hover:underline'}
            >
              Instagram
            </a>
          </LinksSection>
          <LinksSection header={'Overig'}>
            <a
              href={'https://jeugdfilms.pages.dev/Algemene-voorwaarden.pdf'}
              className={'block hover:underline'}
            >
              Algemene voorwaarden
            </a>
            <a
              href={'https://jeugdfilms.pages.dev/Privacyverklaring.pdf'}
              className={'block hover:underline'}
            >
              Privacyverklaring
            </a>
            <PaymentBadges />
          </LinksSection>
        </div>
      </div>
      <div className={'flex flex-col items-center justify-center p-8'}>
        <Link className="inline-flex items-center mx-auto my-4" href="/">
          <Logo />
        </Link>
        {buildTime && (
          <div className={'text-xs text-gray-400'}>
            versie: {buildVersion} (laatst geüpdatet:{' '}
            {formatDistance(new Date(buildTime), new Date(), {
              addSuffix: true,
              locale: nl,
            })}
            )
          </div>
        )}
      </div>
    </footer>
  )
}

function LinksSection({ header, children }: { header: string; children?: ReactNode }) {
  if (!children || React.Children.count(children) === 0) {
    return <></>
  }
  return (
    <div className={'break-inside-avoid-column mb-4 lg:pr-2'}>
      <strong className={'block'}>{header}</strong>
      {children}
    </div>
  )
}

function DonateWidget() {
  if (process.env.NODE_ENV === 'development') return null
  return (
    <iframe
      loading="lazy"
      className={'h-[204px] rounded'}
      src={
        'https://www.gofundme.com/f/xytzme-doneer-voor-filmprojecten-op-scholen/widget/medium?sharesheet=fundraiser sidebar'
      }
    />
  )
}
